import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { usePlaces } from '@/hooks/usePlaces';
import { PlaceCard } from '@/components/PlaceCard';
import { AlertIcon } from '@/components/Icons';
import type { PlaceWithState } from '@/types/api';
import {
  STORM_RESILIENCE,
  SHELTER_SLUG,
  EVACUATION_ZONE_LOOKUP_URL,
  CHARLOTTE_COUNTY_EM_URL,
  CHARLOTTE_COUNTY_STORM_RECOVERY_URL,
  BABCOCK_RANCH_HURRICANE_PREP_URL,
  BABCOCK_RANCH_HURRICANE_PROTECTIONS_URL,
  HURRICANE_CHECKLIST,
  CHECKLIST_STORAGE_KEY,
  PREP_LOCALITY_ORDER,
  groupChecklistByLocality,
  isStormSupplyPlace,
} from '@/lib/hurricanePrep';
import {
  groupByLocality,
  LOCALITY_BLURBS,
  LOCALITY_LABELS,
  type LocalityGroup,
} from '@shared/locality';

function loadChecked(): Record<string, boolean> {
  try {
    const raw = window.localStorage.getItem(CHECKLIST_STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function saveChecked(next: Record<string, boolean>) {
  try {
    window.localStorage.setItem(CHECKLIST_STORAGE_KEY, JSON.stringify(next));
  } catch {
    /* private mode — checklist just won't persist across visits */
  }
}

const TOTAL_ITEMS = HURRICANE_CHECKLIST.reduce((n, g) => n + g.items.length, 0);
const CHECKLIST_BY_LOCALITY = groupChecklistByLocality();

export function PreparePage() {
  const { places, loading } = usePlaces();
  const [checked, setChecked] = useState<Record<string, boolean>>({});

  useEffect(() => {
    setChecked(loadChecked());
  }, []);

  const checkedCount = Object.values(checked).filter(Boolean).length;

  function toggle(id: string) {
    setChecked((prev) => {
      const next = { ...prev, [id]: !prev[id] };
      saveChecked(next);
      return next;
    });
  }

  function resetChecklist() {
    setChecked({});
    saveChecked({});
  }

  const supplyPlaces = useMemo(() => (places ?? []).filter(isStormSupplyPlace), [places]);
  const suppliesByLocality = useMemo(() => groupByLocality(supplyPlaces), [supplyPlaces]);

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Hurricane Prep</h1>
        <p className="page-subtitle">Before the storm — checklist, shelter, and supplies</p>
      </div>

      <div className="card" style={{ borderLeft: '4px solid var(--color-pine)' }}>
        <h2 style={{ color: 'var(--color-pine)', display: 'flex', alignItems: 'center', gap: 6 }}>
          <AlertIcon />
          Babcock Ranch's track record
        </h2>
        <p style={{ fontSize: 14, marginTop: 6, color: 'var(--color-ink)' }}>{STORM_RESILIENCE}</p>
      </div>

      <div className="card" style={{ marginTop: 10 }}>
        <h2>Know your evacuation zone</h2>
        <p style={{ fontSize: 14, marginTop: 6, color: 'var(--color-ink)', lineHeight: 1.5 }}>
          Evacuation zones are about storm surge specifically, not overall storm risk — wind, inland
          flooding, and road closures aren't zone-gated, and a zone is a different thing from a flood
          zone. Babcock Ranch is widely reported as inland and elevated enough to sit outside Charlotte
          County's mandatory surge-evacuation zones, but the only way to know your exact zone is to look
          it up.
        </p>
        <div className="action-row" style={{ marginTop: 10 }}>
          <a className="action-btn" href={EVACUATION_ZONE_LOOKUP_URL} target="_blank" rel="noreferrer">
            Look up your zone
          </a>
          <a className="action-btn" href={CHARLOTTE_COUNTY_EM_URL} target="_blank" rel="noreferrer">
            County Emergency Mgmt
          </a>
        </div>
      </div>

      <div className="card" style={{ marginTop: 10 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
          <h2>Before the storm</h2>
          <span style={{ fontSize: 13, color: 'var(--color-ink-muted)' }}>
            {checkedCount} / {TOTAL_ITEMS}
          </span>
        </div>
        {PREP_LOCALITY_ORDER.map((locality) => (
          <PrepLocalityBlock
            key={locality}
            locality={locality}
            groups={CHECKLIST_BY_LOCALITY[locality]}
            supplies={suppliesByLocality[locality]}
            loadingSupplies={loading}
            checked={checked}
            onToggle={toggle}
          />
        ))}
        {checkedCount > 0 && (
          <button type="button" className="btn" style={{ marginTop: 14 }} onClick={resetChecklist}>
            Reset checklist
          </button>
        )}
      </div>

      <div className="card" style={{ marginTop: 10 }}>
        <h2>Shelter & official resources</h2>
        <p style={{ fontSize: 13, color: 'var(--color-ink-muted)', marginTop: 4, lineHeight: 1.5 }}>
          Shelters don't open for every storm — always confirm activation with Charlotte County
          Emergency Management before driving over.
        </p>
        <div className="action-row" style={{ marginTop: 10, flexWrap: 'wrap' }}>
          <Link className="action-btn" to={`/places/${SHELTER_SLUG}`}>
            Nearest shelter
          </Link>
          <Link className="action-btn" to="/emergency">
            Emergency info
          </Link>
          <a className="action-btn" href={CHARLOTTE_COUNTY_STORM_RECOVERY_URL} target="_blank" rel="noreferrer">
            County storm recovery
          </a>
          <a className="action-btn" href={BABCOCK_RANCH_HURRICANE_PREP_URL} target="_blank" rel="noreferrer">
            Babcock Ranch prep guide
          </a>
          <a className="action-btn" href={BABCOCK_RANCH_HURRICANE_PROTECTIONS_URL} target="_blank" rel="noreferrer">
            Babcock Ranch shutter/window rules
          </a>
        </div>
      </div>
    </div>
  );
}

function PrepLocalityBlock({
  locality,
  groups,
  supplies,
  loadingSupplies,
  checked,
  onToggle,
}: {
  locality: LocalityGroup;
  groups: ReturnType<typeof groupChecklistByLocality>[LocalityGroup];
  supplies: PlaceWithState[];
  loadingSupplies: boolean;
  checked: Record<string, boolean>;
  onToggle: (id: string) => void;
}) {
  return (
    <section
      className="locality-section"
      style={{ marginTop: 16 }}
      aria-labelledby={`prep-locality-${locality}`}
    >
      <div className="locality-header">
        <h2 id={`prep-locality-${locality}`} className="locality-header__title">
          {LOCALITY_LABELS[locality]}
          <span className="locality-header__count">
            {groups.reduce((n, g) => n + g.items.length, 0)}
          </span>
        </h2>
        <p className="locality-header__blurb">{LOCALITY_BLURBS[locality]}</p>
      </div>
      {groups.map((group) => (
        <div key={group.id} style={{ marginTop: 14 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-ink-muted)', marginBottom: 6 }}>
            {group.title}
          </div>
          {group.items.map((item) => (
            <label
              key={item.id}
              className="list-row"
              style={{ padding: '8px 0', gap: 10, cursor: 'pointer' }}
            >
              <input
                type="checkbox"
                checked={!!checked[item.id]}
                onChange={() => onToggle(item.id)}
                style={{ width: 18, height: 18, flexShrink: 0 }}
              />
              <span
                style={{
                  textDecoration: checked[item.id] ? 'line-through' : undefined,
                  color: checked[item.id] ? 'var(--color-ink-muted)' : 'var(--color-ink)',
                }}
              >
                {item.label}
              </span>
            </label>
          ))}
        </div>
      ))}
      <div className="section-title" style={{ marginTop: 18 }}>
        Get supplies nearby
      </div>
      {loadingSupplies && <div className="skeleton" style={{ height: 100 }} />}
      {!loadingSupplies && supplies.length === 0 && (
        <p className="empty-state">No supply places listed yet.</p>
      )}
      {supplies.map((p) => (
        <PlaceCard key={p.id} place={p} />
      ))}
    </section>
  );
}
